import type { FormInstance, FormProps } from 'antd';
import type { Rule } from 'antd/es/form';

/**
 * Quy ước form chung cho mọi trang xác thực (đặc tả §3.1). Rải vào <Form {...AUTH_FORM_PROPS}>.
 * `noValidate`: ô type="email" không bật bong bóng lỗi tiếng Anh của trình duyệt chen ngang
 * thông báo tiếng Việt của antd.
 */
export const AUTH_FORM_PROPS: Pick<FormProps, 'layout' | 'requiredMark' | 'size' | 'noValidate'> = {
  layout: 'vertical',
  requiredMark: false,
  size: 'large',
  noValidate: true,
};

/** Toàn bộ câu kiểm tra (đặc tả §3.4). Mỗi câu nói rõ cách sửa. */
export const VALIDATION = {
  emailRequired: 'Vui lòng nhập email.',
  emailFormat: 'Email chưa đúng định dạng, ví dụ ten@congty.vn.',
  emailMax: 'Email tối đa 255 ký tự.',
  passwordRequired: 'Vui lòng nhập mật khẩu.',
  newPasswordRequired: 'Vui lòng nhập mật khẩu mới.',
  passwordMin: 'Mật khẩu cần ít nhất 8 ký tự.',
  passwordMax: 'Mật khẩu tối đa 32 ký tự.',
  codeRequired: 'Vui lòng nhập mã xác nhận.',
  codeFormat: 'Mã gồm đúng 4 chữ số.',
  nameRequired: 'Vui lòng nhập họ tên.',
  nickNameRequired: 'Vui lòng nhập biệt danh.',
  nickNameLength: 'Biệt danh cần 2–32 ký tự.',
  phoneRequired: 'Vui lòng nhập số điện thoại.',
  phoneLength: 'Số điện thoại cần 2–32 ký tự.',
  tiktokRequired: 'Vui lòng nhập TikTok ID.',
  tiktokFormat: 'TikTok ID chỉ gồm chữ, số, dấu chấm và gạch dưới.',
  tiktokTaken: 'TikTok ID này đã có người dùng.',
  agreeRules: 'Bạn cần đồng ý với Quy tắc cộng đồng để tiếp tục.',
} as const;

/** Trim + chữ thường, như app (email so khớp chính xác trong DB). */
export const normalizeEmail = (value?: string) => (value || '').trim().toLowerCase();

/** Bỏ mọi ký tự không phải số (ô mã 4 số). */
export const normalizeDigits = (value?: string) => (value || '').replace(/\D/g, '');

/**
 * Lỗi "bắt buộc" chỉ hiện khi bấm gửi, không hiện chỉ vì con trỏ đi qua một ô trống (vd. ô email
 * tự có con trỏ trên màn rộng rồi người dùng bấm thẳng sang ô mật khẩu, hoặc bấm nút mắt).
 * 'onSubmit' không phải sự kiện của ô nên rc-field-form bỏ luật này khi kiểm tra lúc blur/gõ;
 * lúc gửi form hoặc gọi thẳng validateFields (sửa ô đang báo lỗi) thì vẫn chạy mọi luật.
 */
export const ON_SUBMIT = 'onSubmit';

export const withRequiredOnSubmit = (rules: Rule[]): Rule[] =>
  rules.map((rule) =>
    typeof rule === 'object' && rule.required ? { ...rule, validateTrigger: ON_SUBMIT } : rule
  );

/**
 * Đặt giá trị một ô, xoá lỗi đang hiện nếu có. Thay cho form.setFieldValue: hàm đó luôn gửi kèm
 * `errors: []` mới, và với ô chưa từng kiểm tra thì rc-field-form 2.7 in ra console lỗi đỏ
 * "There may be circular references" (dương tính giả khi so meta).
 */
export const setFieldQuietly = (form: FormInstance, name: string, value: unknown) => {
  const hasErrors = form.getFieldError(name).length > 0;
  form.setFields([{ name, value, ...(hasErrors ? { errors: [] } : {}) }]);
};

/** Dùng kèm `validateFirst` và `validateTrigger="onBlur"` trên Form.Item email. */
export const emailRules: Rule[] = withRequiredOnSubmit([
  { required: true, message: VALIDATION.emailRequired },
  { type: 'email', message: VALIDATION.emailFormat },
  { max: 255, message: VALIDATION.emailMax },
]);

/**
 * Mật khẩu mới (đăng ký, đặt lại): 8–32 như DTO đăng ký. Backend đặt lại chỉ cần ≥6 nhưng
 * chặt hơn phía client không phá tương thích. KHÔNG đặt maxLength trên ô (dán bị cắt ngầm).
 */
export const newPasswordRules: Rule[] = withRequiredOnSubmit([
  { required: true, message: VALIDATION.newPasswordRequired },
  { min: 8, message: VALIDATION.passwordMin },
  { max: 32, message: VALIDATION.passwordMax },
]);

/** Mã xác nhận 4 số (users/verify, auth/verify/code-forgot). */
export const codeRules: Rule[] = [
  { required: true, message: VALIDATION.codeRequired },
  { pattern: /^\d{4}$/, message: VALIDATION.codeFormat },
];

/** Cùng regex với backend (REGEX_USERNAME) và app. */
export const TIKTOK_ID_PATTERN = /^[a-zA-Z0-9_.]+$/;
